import { useEffect, useState } from "react";

/**
 * The animated story on the dark side of Login and Register.
 *
 * DIFFERENT ON EACH PAGE, on purpose:
 *  - Login  → the Learning Library: a returning student sees their book open at
 *             the page they stopped on, with chapters ticking off.
 *  - Sign-up → the AI tutor: a search box types a teaser question, then the
 *             chat answers — a taste of the first minute after joining.
 * One short message rotates beneath. Brand palette, no stock images.
 */
const MESSAGES = {
    login: [
        "Your Library remembers the page you stopped reading.",
        "Your coaching added new notes since your last visit.",
        "Pick up your Research Studio project where you left it.",
    ],
    register: [
        "One question away from your first clear answer.",
        "Ask in Hinglish, Hindi or English — it understands.",
        "Your Library, mock tests and study tools unlock inside.",
    ],
} as const;

/** Teasers typed into the search box, one at a time. */
const SEARCHES = [
    "What will you ask first…?",
    "Why does the sky turn red at sunset?",
    "gati ka pehla niyam kya hai?",
];

const useRotating = (length: number, ms: number) => {
    const [index, setIndex] = useState(0);
    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const timer = window.setInterval(() => setIndex((i) => (i + 1) % length), ms);
        return () => window.clearInterval(timer);
    }, [length, ms]);
    return index;
};

export const AuthShowcase = ({ variant }: { variant: "login" | "register" }) => {
    const messageIndex = useRotating(MESSAGES[variant].length, 3800);
    const searchIndex = useRotating(SEARCHES.length, 4200);

    return (
        <div className={`auth-showcase auth-showcase--${variant}`} aria-hidden="true">
            <div className="auth-showcase__glow" />

            {variant === "login" ? (
                <>
                    <div className="auth-showcase__card auth-showcase__book">
                        <div className="auth-showcase__bar"><span className="auth-showcase__icon">📖</span> Learning Library <i>continue</i></div>
                        <p className="auth-showcase__book-title">Physics · Laws of Motion</p>
                        <p className="auth-showcase__book-meta">Page 42 of 120 · you stopped here</p>
                        <span className="auth-showcase__meter"><b /></span>
                        <ul className="auth-showcase__chapters">
                            <li className="is-done">Ch 1 · Force and inertia</li>
                            <li className="is-done">Ch 2 · Momentum</li>
                            <li className="is-now">Ch 3 · Action and reaction</li>
                        </ul>
                    </div>
                    <div className="auth-showcase__card auth-showcase__stat">
                        <small>New from your coaching</small>
                        <strong>3 notes</strong>
                        <span className="auth-showcase__pill">added this week</span>
                    </div>
                </>
            ) : (
                <>
                    <div className="auth-showcase__search">
                        <span className="auth-showcase__search-icon" />
                        <span className="auth-showcase__search-text" key={searchIndex}>{SEARCHES[searchIndex]}</span>
                    </div>
                    <div className="auth-showcase__card auth-showcase__chat">
                        <div className="auth-showcase__bar"><span className="auth-showcase__dot" /> AI Tutor <i>online</i></div>
                        <p className="auth-showcase__bubble auth-showcase__bubble--you">physics me gati ka niyam kya hai?</p>
                        <p className="auth-showcase__bubble auth-showcase__bubble--ai">Newton ke 3 niyam hain. Pehla: jab tak bal na lage…</p>
                    </div>
                    <div className="auth-showcase__card auth-showcase__stat">
                        <small>Study tools unlocked</small>
                        <strong>6</strong>
                        <span className="auth-showcase__meter"><b /></span>
                    </div>
                </>
            )}

            <p className="auth-showcase__message" key={`${variant}-${messageIndex}`}>{MESSAGES[variant][messageIndex]}</p>
        </div>
    );
};
